import { useCallback, useState } from "react";

import { useMedplum } from "@medplum/react";

import { exportPracticeLedgerCsv } from "@/billing/services/practice-ledger";
import type {
  PracticeLedgerDateRange,
  PracticeLedgerSearch,
  PracticeLedgerSortState,
} from "@/billing/types/practice-ledger";
import { downloadCsv } from "@/shared/utils/csv";
import { toast } from "@/utils/notifications";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

const EXPORT_TOAST_ID = "practice-ledger-csv-export";
const UNEXPECTED_EXPORT_ERROR_MESSAGE =
  "Could not prepare the ledger CSV. Try again or contact support if this continues.";

interface UsePracticeLedgerCsvExportParams {
  practiceId: string | undefined;
  dateRange: PracticeLedgerDateRange;
  search: PracticeLedgerSearch | undefined;
  sort: PracticeLedgerSortState;
}

function getExportErrorMessage(reason: "missing_practice_id" | "load_failed"): string {
  if (reason === "missing_practice_id") {
    return "A practice must be selected before exporting the ledger.";
  }
  return "Could not load the ledger for export. Try again or contact support if this continues.";
}

/**
 * Downloads the practice Ledger CSV for the current Show: window, search, and sort — the full
 * matching set, not the visible page.
 */
export function usePracticeLedgerCsvExport({
  practiceId,
  dateRange,
  search,
  sort,
}: UsePracticeLedgerCsvExportParams) {
  const medplum = useMedplum();
  const [isExporting, setIsExporting] = useState(false);

  const handleExportCsv = useCallback(async () => {
    setIsExporting(true);
    toast({
      id: EXPORT_TOAST_ID,
      title: "Preparing export",
      message: "Building your ledger CSV. This may take a moment for large windows.",
      type: "info",
    });

    try {
      const result = await exportPracticeLedgerCsv(medplum, practiceId ?? "", dateRange, {
        search,
        sort,
      });

      if (!result.ok) {
        toast({
          id: EXPORT_TOAST_ID,
          title: "Export failed",
          message: getExportErrorMessage(result.reason),
          type: "error",
          autoClose: false,
        });
        return;
      }

      downloadCsv(result.data.csvContent, result.data.filename);

      const rowCount = result.data.rowCount;
      const countMessage =
        rowCount === 0
          ? "Your CSV was downloaded with headers only (no ledger activity matches)."
          : `Your CSV was downloaded with ${rowCount} row${rowCount === 1 ? "" : "s"}.`;

      toast({
        id: EXPORT_TOAST_ID,
        title: "Export complete",
        message: countMessage,
        type: "success",
        autoClose: 3000,
      });
    } catch (error) {
      captureExceptionWithoutPhi("Failed to export the practice ledger CSV", error, {
        practiceId,
      });
      toast({
        id: EXPORT_TOAST_ID,
        title: "Export failed",
        message: UNEXPECTED_EXPORT_ERROR_MESSAGE,
        type: "error",
        autoClose: false,
      });
    } finally {
      setIsExporting(false);
    }
  }, [dateRange, medplum, practiceId, search, sort]);

  return { isExporting, handleExportCsv };
}
